import { useEffect, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import CircularLoading from '../../../../../components/CircularProgress'
import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../components/SelectSearch'
import { validateMinMaxValue } from '../../../../../utils/validateMinMaxValue'
import { message } from 'antd'
import { IUserLists } from '../../../../../types/user'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import UserSelectSearch from '../../../../../components/SelectSearch/UserSelectSearch'

interface ISendLeadFormModalProps {
  isOpen: boolean
  objectIds: string[]
  onClose: (visible: boolean) => void
  onUpdateLeadListSuccess: () => void
}

interface ISendLeadFormModal {
  assignToObjectId: string
  expiredDays: number
  note?: string
  date?: any
  objectIds: string[]
}

const getSendLeadFormValidationSchema = () => {
  return Yup.object({
    assignToObjectId: Yup.string().required('กรุณาเลือกพนักงาน'),
    expiredDays: Yup.number().required('กรุณากรอกจำนวนวันที่หมดอายุ'),
    // note: Yup.string().required('กรุณากรอกหมายเหตุ'),
    // date: Yup.string().nullable().notRequired(),
    objectIds: Yup.array().of(Yup.string()).required('กรุณาเลือก Lead'),
  })
}

export const SendLeadFormModal = (props: ISendLeadFormModalProps) => {
  const { isOpen, onClose, onUpdateLeadListSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchAssignTo, setSearchAssignTo] = useState<string>('')

  useEffect(() => {
    setIsLoading(true)
    formik.setFieldValue('objectIds', props.objectIds)
    // formik.setFieldValue('date', dayjs())
    setIsLoading(false)
  }, [props.objectIds])

  const formik = useFormik<ISendLeadFormModal>({
    initialValues: {
      assignToObjectId: '',
      expiredDays: 1,
      note: '',
      // date: dayjs(),
      objectIds: [],
    } as ISendLeadFormModal,
    validationSchema: getSendLeadFormValidationSchema(),
    onSubmit: async (values: ISendLeadFormModal) => {
      // if(!values.date) {
      //   message.error('กรุณาเลือกวันที่')
      //   setIsLoading(false)
      //   return
      // }
      setIsLoading(true)
      try {
        await axiosInstance.put('/lead/change-assign-to', {
          ...values,
        })
        message.success('ส่งต่อ Lead สำเร็จ')
        formik.resetForm()
        onClose(false)
        onUpdateLeadListSuccess()
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  const assignToList = useAxios<PaginationResponseInterface<IUserLists>>({
    method: 'GET',
    url: '/users/merchant',
    params: {
      page: 1,
      perPage: 5,
      search: searchAssignTo,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAssignTo = async (value: string) => {
    setSearchAssignTo(value)
    await assignToList.mutate()
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="lg">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>ส่งต่อ Lead</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <UserSelectSearch
                label={'กำหนดผู้ดูแล Lead'}
                onSelect={(e: any) =>
                  formik.setFieldValue('assignToObjectId', e?.value.objectId)
                }
                name="assignToObjectId"
                helperText={
                  formik.touched.assignToObjectId &&
                  (formik.errors.assignToObjectId as string)
                }
                error={
                  !!(
                    formik.touched.assignToObjectId &&
                    formik.errors.assignToObjectId
                  )
                }
                onBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <TextField
                id="expiredDays"
                fullWidth
                label="จำนวนวันที่หมดอายุ"
                name="expiredDays"
                helperText={
                  formik.touched.expiredDays &&
                  (formik.errors.expiredDays as string)
                }
                error={
                  !!(formik.touched.expiredDays && formik.errors.expiredDays)
                }
                onBlur={formik.handleBlur}
                type={'number'}
                onKeyPress={(e) => {
                  if (['+', '-', 'e', '*', '.'].includes(e.key)) {
                    e.preventDefault()
                  }
                }}
                onChange={(e) => {
                  formik.setFieldValue(
                    'expiredDays',
                    validateMinMaxValue(e.target.value, 0, 1000) || '',
                  )
                }}
                value={formik.values.expiredDays}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  disablePast
                  format={ONLY_DATE_TIME_FORMAT}
                  ampm={false}
                  label="เลือกวันที่ติดตาม"
                  name="date"
                  sx={{ width: '100%' }}
                  //value={formik.values.date ? dayjs(formik.values.date) : null}
                  onChange={(e) => formik.setFieldValue('date', e)}
                  slotProps={{
                    textField: {
                      helperText: '',
                      error: false,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                multiline={true}
                rows={3}
                fullWidth
                label={`หมายเหตุ`}
                name="note"
                helperText={
                  formik.touched.note && (formik.errors.note as string)
                }
                error={!!(formik.touched.note && formik.errors.note)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                บันทึก
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
